import { Inmate } from '@reliance/types/models/Inmate';
import { InmateFilters } from '@reliance/types/filters/InmateFilters';
import {
  type ColumnFiltersState,
  type PaginationState,
  type SortingState,
  createColumnHelper,
} from '@tanstack/react-table';
import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { formatInmateAVBalance, formatInmateBalance } from '../../format/InmateBalance';
import { formatFacility } from '../../format/Facility';
import { formatPhoneNumber } from '../../format/PhoneNumber';
import { formatDate } from '../../format/DateTime';
import { useInmates } from '../../hooks/Inmate';
import { useNotifyTabLoading } from '../../hooks/useTabLoadingReporter';
import BaseHtmlTable, { buildServerTableFilterRecord } from '../../table/BaseHtmlTable';

const STATUS_OPTIONS = [
  { value: '', label: '' },
  { value: 'Enabled', label: 'Enabled' },
  { value: 'Disabled', label: 'Disabled' }
];

export type InmateListRow = Inmate & {
  fullName?: string;
  facilityTitle?: string;
  facility?: { id: number; name?: string; city?: string; state?: string };
  device?: { mobileDevice?: { name?: string } };
};

type InmatesTableProps = {
  initialFilters?: Record<string, unknown>;
  columns?: string[];
  /** When set (e.g. facility detail inmates tab), drives that tab's loading spinner. */
  notifyTabLoadingId?: string | null;
};

const DEFAULT_VISIBLE_COLUMNS = [
  'dateAdded',
  'status',
  'phoneStatus',
  'textStatus',
  'facility',
  'firstName',
  'lastName',
  'account',
  'device',
  'msgBalance',
  'actions'
];

const InmatesTable: React.FC<InmatesTableProps> = (props) => {
  const { initialFilters = {}, columns: columnsProp, notifyTabLoadingId } = props;
  const [pagination, setPagination] = useState<PaginationState>({ pageIndex: 0, pageSize: 100 });
  const [sorting, setSorting] = useState<SortingState>([{ id: 'dateAdded', desc: true }]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);

  const stableInitial = useMemo(() => ({ ...initialFilters }), [JSON.stringify(initialFilters)]);

  const visibleColumns = columnsProp ?? DEFAULT_VISIBLE_COLUMNS;
  const hiddenColumns = useMemo(
    () =>
      DEFAULT_VISIBLE_COLUMNS
        .filter((c) => c !== 'actions')
        .filter((c) => !visibleColumns.includes(c)),
    [visibleColumns]
  );

  const columnHelper = createColumnHelper<InmateListRow>();
  const columnDefs = useMemo(
    () => [
      columnHelper.accessor('dateAdded', {
        header: () => 'Date Added',
        cell: (info) => {
          const val = info.getValue();
          if (val == null) {
            return '-';
          }
          const dateStr = typeof val === 'object' && val !== null && 'date' in val ? (val as { date: string }).date : String(val);
          return formatDate(dateStr);
        },
        meta: { filterComponent: 'DateRangeFilter', sortName: 'dateAdded' }
      }),
      columnHelper.accessor('status', {
        header: () => 'Status',
        cell: (info) => info.getValue() ?? '-',
        meta: { filterComponent: 'SelectFilter', filterOptions: STATUS_OPTIONS }
      }),
      columnHelper.accessor('phoneStatus', {
        header: () => 'Phone Status',
        cell: (info) => info.getValue() ?? '-',
        meta: { filterComponent: 'SelectFilter', filterOptions: STATUS_OPTIONS }
      }),
      columnHelper.accessor('textStatus', {
        header: () => 'Text Status',
        cell: (info) => info.getValue() ?? '-',
        meta: { filterComponent: 'SelectFilter', filterOptions: STATUS_OPTIONS }
      }),
      columnHelper.display({
        id: 'facility',
        header: () => 'Facility',
        cell: ({ row }) => {
          const f = row.original.facility;
          if (!f) {
            return 'Unassigned';
          }
          const label = row.original.facilityTitle ?? (f.name ? `${f.name}${f.state ? ` (${f.state})` : ''}` : `Facility ${f.id}`);
          return <Link to={`/facilities/view/${f.id}`}>{label}</Link>;
        },
        meta: { filterComponent: 'FacilityIdsFilter', sortName: 'facility.name' }
      }),
      columnHelper.accessor('firstName', {
        header: () => 'First Name',
        cell: (info) => info.getValue() ?? '-',
        meta: { filterComponent: 'TextFilter' }
      }),
      columnHelper.accessor('lastName', {
        header: () => 'Last Name',
        cell: (info) => info.getValue() ?? '-',
        meta: { filterComponent: 'TextFilter' }
      }),
      columnHelper.accessor('account', {
        header: () => 'Account',
        cell: (info) => info.getValue() ?? '-',
        meta: { filterComponent: 'TextFilter' }
      }),
      columnHelper.display({
        id: 'device',
        header: () => 'Device',
        cell: ({ row }) => {
          const { phoneNumber, device } = row.original;
          if (!phoneNumber && !device) {
            return '-';
          }
          const parts = [];
          if (phoneNumber) {
            parts.push(formatPhoneNumber(phoneNumber));
          }
          if (device?.mobileDevice?.name) {
            parts.push(` (${device.mobileDevice.name})`);
          }
          return parts.length ? parts.join('') : '-';
        }
      }),
      columnHelper.display({
        id: 'msgBalance',
        header: () => 'Msg Balance',
        cell: ({ row }) => formatInmateBalance(row.original)
      }),
      columnHelper.display({
        id: 'avBalance',
        header: () => 'AV Balance',
        cell: ({ row }) => formatInmateAVBalance(row.original)
      }),
      columnHelper.display({
        id: 'actions',
        header: () => 'Actions',
        cell: ({ row }) =>
          <Link className="btn btn-secondary btn-sm" to={`/inmates/view/${row.original.id}`}>
            View
          </Link>
      })
    ],
    []
  );

  const filters = useMemo((): InmateFilters => {
    const fromTable = buildServerTableFilterRecord(pagination, columnFilters, sorting, columnDefs);
    return { ...stableInitial, ...fromTable } as InmateFilters;
  }, [stableInitial, pagination, columnFilters, sorting, columnDefs]);

  const inmatesResult = useInmates(filters);

  useNotifyTabLoading(notifyTabLoadingId ?? null, inmatesResult.loading);

  const result = inmatesResult.result;
  const rows: InmateListRow[] = (result?.data ?? []).map((row: Inmate) => {
    const fullName = row.firstName && row.lastName
      ? `${row.firstName} ${row.middleName || ''} ${row.lastName}`.replace(/\s+/g, ' ').trim()
      : undefined;
    return {
      ...row,
      fullName,
      facilityTitle: row.facility ? formatFacility(row.facility) : undefined
    } as InmateListRow;
  });

  return (
    <BaseHtmlTable
      columnDefs={columnDefs}
      rows={rows}
      loading={inmatesResult.loading}
      paginationMode="server"
      page={result?.page ?? pagination.pageIndex + 1}
      totalPages={result?.totalPages}
      total={result?.total}
      perPage={pagination.pageSize}
      serverPagination={pagination}
      onServerPaginationChange={setPagination}
      serverSorting={sorting}
      onServerSortingChange={setSorting}
      serverColumnFilters={columnFilters}
      onServerColumnFiltersChange={setColumnFilters}
      hiddenColumns={hiddenColumns}
      pageSizeOptions={[50, 100, 500]}
    />
  );
};

export default InmatesTable;
